/**
 * PublicReconReport — tokenised, no-login recon report for external payment acceptance.
 * Accessed at /r/:token — no auth, mobile-friendly.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicReconAPI } from '../lib/api';

import {
  FiCheckCircle, FiXCircle, FiEdit2, FiCheck, FiFileText,
  FiAlertTriangle, FiMessageSquare, FiLoader, FiDownload,
} from 'react-icons/fi';
import { BsCash } from 'react-icons/bs';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(amount, currency = 'ZAR') {
  if (amount == null || amount === '') return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(parseFloat(amount));
}

function formatDate(str) {
  if (!str) return '';
  try { return new Date(str).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return str; }
}

function formatDateTime(str) {
  if (!str) return '';
  try { return new Date(str).toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return str; }
}

const DOC_TYPE_LABELS = {
  supplier_invoices: 'Invoice',
  purchase_listing: 'Purchase Listing',
  supplier_statements: 'Statement',
  company_supplier_ledgers: 'Ledger',
};

// ─── MatchCard ────────────────────────────────────────────────────────────────

function MatchCard({ match, allDocs, token: shareToken, onUpdated, readOnly }) {
  const [customAmount, setCustomAmount] = useState('');
  const [editingCustom, setEditingCustom] = useState(false);
  const [notePrompt, setNotePrompt] = useState(null); // { type: 'rejected'|'custom', amount? }
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const ps = match.payment_status;

  const DOC_ORDER = { supplier_invoices: 0, purchase_listing: 1 };
  const linkedDocs = (allDocs || [])
    .filter(d => (match.document_ids || []).includes(d.id))
    .sort((a, b) => {
      const ta = a.user_doc_type || a.detected_doc_type || a.document_type;
      const tb = b.user_doc_type || b.detected_doc_type || b.document_type;
      return (DOC_ORDER[ta] ?? 99) - (DOC_ORDER[tb] ?? 99);
    });

  const save = async (status, amount, note) => {
    setSaving(true);
    try {
      const res = await publicReconAPI.updateMatch(shareToken, match.id, {
        payment_status: status,
        payment_amount: amount,
        payment_note: note,
      });
      onUpdated(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCustomSave = () => {
    const val = parseFloat(customAmount);
    if (!isNaN(val) && val >= 0) {
      setEditingCustom(false);
      setNotePrompt({ type: 'custom', amount: val });
      setNoteText('');
    }
  };

  const handleNoteSave = () => {
    if (!noteText.trim()) return;
    if (notePrompt.type === 'rejected') save('rejected', null, noteText.trim());
    else save('custom', notePrompt.amount, noteText.trim());
    setNotePrompt(null);
    setNoteText('');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 bg-green-50 border-b border-green-100 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 text-sm truncate">{match.supplier_name}</p>
          <p className="text-xs text-green-600 font-medium">Accepted</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-gray-900">
            {fmt(match.payment_amount ?? match.variance)}
          </p>
          {match.payment_amount != null && match.variance != null && match.payment_amount !== match.variance ? (
            <p className="text-xs text-orange-600 font-medium">
              {`-${parseFloat(((1 - match.payment_amount / match.variance) * 100).toFixed(2))}% (${fmt(match.variance - match.payment_amount)} discount)`}
            </p>
          ) : (
            <p className="text-xs text-gray-400">amount to pay</p>
          )}
        </div>
      </div>

      {/* Linked docs */}
      {linkedDocs.length > 0 && (
        <div className="divide-y divide-gray-50">
          {linkedDocs.map(doc => {
            const effType = doc.user_doc_type || doc.detected_doc_type || doc.document_type;
            const amount = doc.user_amount != null ? doc.user_amount : doc.amount;
            return (
              <React.Fragment key={doc.id}>
                <div className="px-4 py-2.5 flex items-start gap-2">
                  <FiFileText className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-medium text-gray-700 truncate">{doc.file_name}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded flex-shrink-0">
                        {DOC_TYPE_LABELS[effType] || effType}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      {doc.supplier_name && <span className="text-xs text-gray-400 truncate">{doc.supplier_name}</span>}
                      {doc.period_date && <span className="text-xs text-gray-400">{formatDate(doc.period_date)}</span>}
                      {amount != null && <span className="text-xs font-medium text-gray-600">{fmt(amount)}</span>}
                    </div>
                  </div>
                  {doc.source !== 'exchange' && (
                    <a
                      href={publicReconAPI.downloadDocUrl(shareToken, doc.id)}
                      download={doc.file_name}
                      className="p-2 text-gray-400 hover:text-blue-600 active:text-blue-800 transition-colors flex-shrink-0"
                      title="Download"
                    >
                      <FiDownload className="w-4 h-4" />
                    </a>
                  )}
                </div>
                {/* Multi-invoice breakdown */}
                {effType === 'supplier_invoices' && (() => {
                  const invLines = (doc.ai_raw?.line_items || []).filter(li => li.type === 'invoice' && li.amount != null);
                  if (invLines.length <= 1) return null;
                  return (
                    <div className="mx-4 mb-2 mt-0.5">
                      <div className="flex items-center gap-2 px-2 py-0.5 text-xs text-gray-400 font-medium border-b border-orange-100 mb-0.5">
                        <span className="w-28 flex-shrink-0">Invoice #</span>
                        <span className="w-24 flex-shrink-0">Date</span>
                        <span className="flex-1 text-right">Amount</span>
                      </div>
                      {invLines.map((li, i) => (
                        <div key={i} className="flex items-center gap-2 px-2 py-1 rounded text-xs bg-orange-50 text-gray-600">
                          <span className="w-28 flex-shrink-0 text-orange-700 font-medium truncate">{li.ref || '—'}</span>
                          <span className="w-24 flex-shrink-0 text-gray-400">{li.date || '—'}</span>
                          <span className="flex-1 text-right font-medium text-gray-700">{fmt(li.amount)}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Payment decision */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-medium text-gray-500">Payment decision</p>

          {readOnly ? (
            ps === 'rejected' ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-lg">
                <FiXCircle className="w-3.5 h-3.5" /> Not paying
              </span>
            ) : ps ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 px-2 py-1 rounded-lg">
                <FiCheckCircle className="w-3.5 h-3.5" />
                {ps === 'custom' ? `Paying ${fmt(match.payment_amount)}` : `Paying ${fmt(match.variance)}`}
              </span>
            ) : (
              <span className="text-xs text-gray-400 italic">No decision</span>
            )
          ) : ps ? (
            <div className="flex items-center gap-2 flex-wrap">
              {ps === 'rejected' ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-lg">
                  <FiXCircle className="w-3.5 h-3.5" /> Not paying
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 px-2 py-1 rounded-lg">
                  <FiCheckCircle className="w-3.5 h-3.5" />
                  {ps === 'custom' ? `Paying ${fmt(match.payment_amount)}` : `Paying ${fmt(match.variance)}`}
                </span>
              )}
              <button
                onClick={() => save(null, null, null)}
                disabled={saving}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >change</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => match.payment_amount != null ? save('custom', match.payment_amount, null) : save('accepted', null, null)}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 active:scale-95 transition-all"
              >
                {saving ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheckCircle className="w-3.5 h-3.5" />}
                Pay {fmt(match.payment_amount ?? match.variance)}
              </button>
              {editingCustom ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    inputMode="decimal"
                    className="w-28 text-sm border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    placeholder="Amount"
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleCustomSave(); if (e.key === 'Escape') setEditingCustom(false); }}
                  />
                  <button onClick={handleCustomSave} disabled={saving}
                    className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <FiCheck className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditingCustom(false)} className="text-sm text-gray-400 px-1">✕</button>
                </div>
              ) : (
                <button
                  onClick={() => { setCustomAmount(String(match.payment_amount ?? match.variance ?? '')); setEditingCustom(true); }}
                  disabled={saving}
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 bg-white border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 active:scale-95 transition-all"
                >
                  <FiEdit2 className="w-3.5 h-3.5" /> Set amount
                </button>
              )}
              <button
                onClick={() => { setNotePrompt({ type: 'rejected' }); setNoteText(''); }}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 active:scale-95 transition-all"
              >
                <FiXCircle className="w-3.5 h-3.5" /> Not paying
              </button>
            </div>
          )}
        </div>

        {/* Note prompt */}
        {notePrompt && (
          <div className="bg-white border border-amber-200 rounded-xl p-3 space-y-2">
            <p className="text-xs font-medium text-gray-700">
              {notePrompt.type === 'rejected'
                ? 'Reason for not paying (required)'
                : `Reason for paying ${fmt(notePrompt.amount)} instead of ${fmt(match.variance)} (required)`}
            </p>
            <textarea
              autoFocus
              rows={3}
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Enter reason..."
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setNotePrompt(null)} className="text-sm text-gray-400 hover:text-gray-600 px-3 py-1.5">Cancel</button>
              <button
                onClick={handleNoteSave}
                disabled={!noteText.trim() || saving}
                className="text-sm font-semibold px-4 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        )}

        {/* Show saved note */}
        {ps && match.payment_note && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500 bg-white rounded-lg p-2 border border-gray-100">
            <FiMessageSquare className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
            <span>{match.payment_note}</span>
          </div>
        )}

        {/* Free-text supplier note — read-only here, editable only from the internal report */}
        {match.supplier_note && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500 bg-white rounded-lg p-2 border border-gray-100">
            <FiMessageSquare className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
            <span>{match.supplier_note}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PublicReconReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Accept flow
  const [accepting, setAccepting] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [acceptorName, setAcceptorName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [payments, setPayments] = useState(null); // payment rows for CSV download

  const load = useCallback(async () => {
    try {
      const res = await publicReconAPI.load(token);
      setData(res.data);
      if (res.data.token?.accepted_at) setAccepted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load report');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleMatchUpdated = (updated) => {
    setData(prev => ({
      ...prev,
      matches: prev.matches.map(m => m.id === updated.id ? { ...m, ...updated } : m),
    }));
  };

  const downloadCsv = (paymentRows, sessionName) => {
    if (!paymentRows || paymentRows.length === 0) return;
    const rows = [
      ['Supplier', 'Bank Name', 'Bank Account', 'Branch Code', 'Amount'],
      ...paymentRows.map(p => [p.supplier_name, p.bank_name, p.bank_account, p.bank_branch, p.amount]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-${sessionName || 'recon'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAccept = async () => {
    if (!acceptorName.trim()) return;
    setAccepting(true);
    try {
      const res = await publicReconAPI.accept(token, acceptorName.trim());
      const paymentRows = res.data.payments || [];
      setPayments(paymentRows);
      setAccepted(true);
      setShowAcceptModal(false);
      setData(prev => ({
        ...prev,
        token: {
          ...prev.token,
          accepted_at: new Date().toISOString(),
          accepted_by_name: acceptorName.trim(),
        },
      }));
      // Auto-download payment CSV
      downloadCsv(paymentRows, data?.session?.name);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <FiLoader className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <FiAlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Link unavailable</h2>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const { session, matches, allDocs, token: tokenInfo } = data;

  const allDecided = matches.length > 0 && matches.every(m => m.payment_status != null);
  // rejected → 0, custom → payment_amount, otherwise use payment_amount if pre-set by discount else variance
  const totalPayable = matches.reduce((s, m) => {
    if (m.payment_status === 'rejected') return s;
    if (m.payment_status === 'custom') return s + (parseFloat(m.payment_amount) || 0);
    return s + (parseFloat(m.payment_amount ?? m.variance) || 0);
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10 shadow-sm">
        <div className="max-w-lg mx-auto">
          <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-0.5">Supplier Recon Report</p>
          <h1 className="text-base font-bold text-gray-900 leading-tight">{session.name}</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {formatDate(session.period_start)} – {formatDate(session.period_end)}
          </p>
          {tokenInfo.assigned_to_name && (
            <p className="text-xs text-gray-400 mt-1">Assigned to: <span className="font-medium text-gray-600">{tokenInfo.assigned_to_name}</span></p>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {/* Summary card */}
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <BsCash className="w-5 h-5 text-amber-500" />
            <div>
              <p className="text-xs text-gray-400">Total Amount to Pay</p>
              <p className="text-lg font-bold text-gray-900">{fmt(totalPayable)}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Suppliers</p>
            <p className="text-lg font-bold text-gray-900">{matches.length}</p>
          </div>
        </div>

        {/* Match cards */}
        {matches.map(match => (
          <MatchCard
            key={match.id}
            match={match}
            allDocs={allDocs}
            token={token}
            onUpdated={handleMatchUpdated}
            readOnly={accepted}
          />
        ))}

        {/* Accept Recon button */}
        {!accepted && allDecided && (
          <button
            onClick={() => setShowAcceptModal(true)}
            className="w-full py-4 text-sm font-bold bg-green-600 text-white rounded-xl hover:bg-green-700 active:scale-95 transition-all shadow-md flex items-center justify-center gap-2"
          >
            <FiCheckCircle className="w-5 h-5" />
            Accept Recon &amp; Approve Payment
          </button>
        )}

        {!accepted && !allDecided && matches.length > 0 && (
          <p className="text-center text-xs text-gray-400 py-2">
            Make a payment decision for all {matches.length} suppliers to finalise.
          </p>
        )}

        {/* Audit footer */}
        {accepted && tokenInfo.accepted_at && (
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-4 text-center space-y-2">
            <FiCheckCircle className="w-6 h-6 text-green-600 mx-auto" />
            <p className="text-sm font-semibold text-green-800">Recon Accepted &amp; Approved for Payment</p>
            <p className="text-xs text-green-700">
              Accepted by <span className="font-semibold">{tokenInfo.accepted_by_name || tokenInfo.assigned_to_name}</span>
            </p>
            <p className="text-xs text-green-600">{formatDateTime(tokenInfo.accepted_at)}</p>
            {payments && payments.length > 0 && (
              <button
                onClick={() => downloadCsv(payments, session.name)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors mt-1"
              >
                <FiDownload className="w-4 h-4" />
                Download Payment File
              </button>
            )}
          </div>
        )}

        <p className="text-center text-xs text-gray-300 pb-4">Powered by Doxolve</p>
      </div>

      {/* Accept confirmation modal */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 space-y-4 shadow-2xl">
            <div className="text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FiCheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Confirm Payment Acceptance</h3>
              <p className="text-sm text-gray-500 mt-1">
                Total to pay: <span className="font-semibold text-gray-800">{fmt(totalPayable)}</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                This action cannot be undone. Your name will be recorded for audit purposes.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Your full name *</label>
              <input
                type="text"
                autoFocus
                value={acceptorName}
                onChange={e => setAcceptorName(e.target.value)}
                placeholder="e.g. John Smith"
                className="w-full text-sm border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowAcceptModal(false)}
                className="flex-1 py-3 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={!acceptorName.trim() || accepting}
                className="flex-1 py-3 text-sm font-bold bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-1"
              >
                {accepting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />}
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
